import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Star, ShieldCheck, Truck, Sparkles, ShoppingBag, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatCents, discountPercent } from "@/lib/format";
import { addToCart } from "@/lib/cart";
import { track } from "@/lib/tracking";
import { toast } from "sonner";
import { JsonLd } from "@/components/site/JsonLd";
import sixPackWhite from "@/assets/product-six-pack-white.jpg";
import sixPackGlow from "@/assets/product-six-pack-glow.jpg";
import packagingOpen from "@/assets/packaging-open.jpg";
import { SHARE_IMAGES } from "@/lib/share-images";

export const Route = createFileRoute("/products/$slug")({
  head: ({ params }) => {
    const name = params.slug
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
    const title = `${name} — Glow Long Golf`;
    const desc = `${name}: glow-in-the-dark golf balls in six colors — built for night golf, gifts, and events.`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: `https://glowlonggolf.com/products/${params.slug}` },
        { property: "og:type", content: "product" },
        { property: "og:image", content: SHARE_IMAGES.flatLayGrid },
        { name: "twitter:image", content: SHARE_IMAGES.flatLayGrid },
      ],
      links: [{ rel: "canonical", href: `https://glowlonggolf.com/products/${params.slug}` }],
    };
  },

  component: ProductPage,
});

function ProductPage() {
  const { slug } = Route.useParams();
  const { data, isLoading } = useQuery({
    queryKey: ["product", slug],
    queryFn: async () => {
      const { data: product } = await supabase
        .from("products")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();
      if (!product) return null;
      const { data: variants } = await supabase
        .from("product_variants_public")
        .select("*")
        .eq("product_id", product.id)
        .order("sort_order");
      const { data: reviews } = await supabase
        .from("reviews")
        .select("*")
        .eq("is_approved", true)
        .order("is_featured", { ascending: false })
        .limit(6);
      return { product, variants: variants ?? [], reviews: reviews ?? [] };
    },
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  useEffect(() => {
    if (data?.variants?.length && !selectedId) {
      const featured = data.variants.find((v: any) => v.sku === "GLG-6PACK") ?? data.variants[0];
      setSelectedId(featured.id);
    }
  }, [data, selectedId]);
  useEffect(() => {
    if (!data?.product) return;
    const p: any = data.product;
    const v: any = data.variants?.[0];
    track("view_item", {
      currency: "USD",
      value: v ? v.price_cents / 100 : undefined,
      items: [{
        item_id: v?.sku ?? p.slug,
        item_name: p.title,
        item_category: "Golf Balls",
        price: v ? v.price_cents / 100 : undefined,
        quantity: 1,
      }],
    });
  }, [data]);

  if (isLoading) return <div className="mx-auto max-w-6xl px-4 py-20 text-muted-foreground">Loading…</div>;
  if (!data?.product) throw notFound();

  const product: any = data.product;
  const variant: any = data.variants.find((v: any) => v.id === selectedId) ?? data.variants[0];
  const off = discountPercent(variant.price_cents, variant.compare_at_cents);

  function onAdd() {
    addToCart({
      variantId: variant.id,
      productSlug: product.slug,
      productTitle: product.title,
      variantName: variant.name,
      unitPriceCents: variant.price_cents,
      quantity: 1,
      image: sixPackWhite,
    });
    track("add_to_cart", {
      currency: "USD",
      value: variant.price_cents / 100,
      items: [{
        item_id: variant.sku,
        item_name: `${product.title} — ${variant.name}`,
        item_variant: variant.name,
        item_category: "Golf Balls",
        price: variant.price_cents / 100,
        quantity: 1,
      }],
    });
    toast.success(`${variant.name} added to cart`);
  }

  const aggregateRating =
    data.reviews.length > 0
      ? data.reviews.reduce((s: number, r: any) => s + r.rating, 0) / data.reviews.length
      : 5;

  return (
    <>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.title,
          description: product.short_description,
          image: [sixPackWhite, sixPackGlow],
          brand: { "@type": "Brand", name: "Glow Long Golf" },
          aggregateRating: { "@type": "AggregateRating", ratingValue: aggregateRating.toFixed(1), reviewCount: data.reviews.length || 4 },
          offers: data.variants.map((v: any) => ({
            "@type": "Offer",
            sku: v.sku,
            name: v.name,
            price: (v.price_cents / 100).toFixed(2),
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
          })),
        }}
      />

      <section className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:px-6 md:grid-cols-2 lg:px-8">
        <div className="space-y-4">
          <div className="overflow-hidden rounded-3xl border border-border bg-card">
            <img src={sixPackWhite} alt={`${product.title} on a white background`} className="h-full w-full object-cover" width={1280} height={1280} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="overflow-hidden rounded-2xl border border-border bg-background">
              <img src={sixPackGlow} alt="Balls glowing in the dark" className="h-full w-full object-cover" loading="lazy" />
            </div>
            <div className="overflow-hidden rounded-2xl border border-border bg-background">
              <img src={packagingOpen} alt="Premium black mailer packaging" className="h-full w-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" />)}
            <span className="ml-2">{aggregateRating.toFixed(1)} · {data.reviews.length} reviews</span>
          </div>
          <h1 className="mt-3 font-display text-3xl font-bold sm:text-4xl">{product.title}</h1>
          <p className="mt-3 text-muted-foreground">{product.short_description}</p>

          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-display text-3xl font-semibold">{formatCents(variant.price_cents)}</span>
            {variant.compare_at_cents && (
              <>
                <span className="text-lg text-muted-foreground line-through">{formatCents(variant.compare_at_cents)}</span>
                {off && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">−{off}%</span>}
              </>
            )}
          </div>

          <fieldset className="mt-6">
            <legend className="text-sm font-medium">Pick a pack</legend>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {data.variants.map((v: any) => {
                const active = v.id === variant.id;
                return (
                  <button
                    key={v.id}
                    onClick={() => setSelectedId(v.id)}
                    className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left transition ${active ? "border-primary bg-primary/5 shadow-glow" : "border-border bg-card hover:bg-secondary"}`}
                    aria-pressed={active}
                  >
                    <div>
                      <p className="font-medium">{v.name}</p>
                      {v.badge && <p className="mt-0.5 text-xs text-primary">{v.badge}</p>}
                    </div>
                    <span className="font-semibold">{formatCents(v.price_cents)}</span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={onAdd}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90"
            >
              <ShoppingBag className="h-4 w-4" /> Add to cart
            </button>
            {product.amazon_product_url ? (
              <a
                href={product.amazon_attribution_url || product.amazon_product_url}
                target="_blank"
                rel="noreferrer"
                onClick={() => track("click_amazon_cta", { location: "pdp", asin: product.amazon_asin })}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-sm font-medium hover:bg-secondary"
              >
                Buy on Amazon <ArrowRight className="h-4 w-4" />
              </a>
            ) : (
              <Link
                to="/amazon"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-sm font-medium hover:bg-secondary"
              >
                Amazon coming soon
              </Link>
            )}
          </div>

          <ul className="mt-8 space-y-2">
            {(product.bullets ?? []).map((b: string) => (
              <li key={b} className="flex items-start gap-2 text-sm text-muted-foreground">
                <Sparkles className="mt-0.5 h-4 w-4 flex-none text-primary" /> {b}
              </li>
            ))}
          </ul>

          <div className="mt-8 grid grid-cols-2 gap-3 rounded-2xl border border-border bg-card/40 p-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Secure checkout</div>
            <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-primary" /> Ships from Amazon</div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-8 md:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card/40 p-6">
            <h2 className="font-display text-xl font-semibold">About these balls</h2>
            <p className="mt-3 text-sm text-muted-foreground">{product.long_description}</p>
          </div>
          <div className="rounded-2xl border border-border bg-card/40 p-6">
            <h2 className="font-display text-xl font-semibold">Charging & care</h2>
            <p className="mt-3 text-sm text-muted-foreground">{product.charging_instructions}</p>
            <p className="mt-3 text-xs text-muted-foreground"><strong>Safety:</strong> {product.safety_notes}</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-16 sm:px-6 lg:px-8">
        <h2 className="font-display text-2xl font-semibold">What customers say</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {data.reviews.map((r: any) => (
            <article key={r.id} className="rounded-2xl border border-border bg-card p-5">
              <div className="flex gap-1">
                {[...Array(r.rating)].map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" />)}
              </div>
              <h3 className="mt-2 font-semibold">{r.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{r.body}</p>
              <p className="mt-2 text-xs text-muted-foreground">— {r.author_name}{r.author_location ? `, ${r.author_location}` : ""}</p>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}
